// frontend/src/admin/AdminContentEditor.js
import React, { useState, useEffect, useCallback } from 'react';
import './admincontenteditor.css';
import ContentBlockRenderer, { stripHtml } from '../pages/ContentBlockRenderer';
import ArticleForm from './blockforms/ArticleForm';
import ImageForm from './blockforms/ImageForm';
import VideoForm from './blockforms/VideoForm';
import TableForm from './blockforms/TableForm';
import FlowchartForm from './blockforms/FlowchartForm';
import HtmlForm from './blockforms/HtmlForm';
import StepsForm from './blockforms/StepsForm';
import ImageCarouselForm from './blockforms/ImageCarouselForm';
import VideoCarouselForm from './blockforms/VideoCarouselForm';
import ContainerForm from './blockforms/ContainerForm';
import AdminContentFullView from './AdminContentFullView';
import TextForm from './blockforms/TextForm';
import WorksheetForm from './blockforms/WorksheetForm';


const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const LEVEL_ENDPOINTS = {
    topic: (id) => `${API_BASE}/rich-content/topic/${id}`,
    subtopic: (id) => `${API_BASE}/rich-content/subtopic/${id}`,
    chunk: (id) => `${API_BASE}/rich-content/chunk/${id}`,
    micro_chunk: (id) => `${API_BASE}/rich-content/micro-chunk/${id}`,
    qa: (id, section) => `${API_BASE}/predefined-qa-rich-content/qa/${id}/${section}`
};

// Single seam: which backend a block create/update/delete/reorder should hit.
// Teaching levels -> /rich-content ; QA -> /predefined-qa-rich-content.
const blockApiBase = (level) =>
    level === 'qa' ? `${API_BASE}/predefined-qa-rich-content` : `${API_BASE}/rich-content`;

const CHILD_LEVEL = { topic: 'subtopic', subtopic: 'chunk', chunk: 'micro_chunk' };
const CHILD_LABEL = { topic: 'Subtopic', subtopic: 'Chunk', chunk: 'Micro Chunk' };

const CHILD_CONTAINER_ENDPOINT = {
    topic: (id) => `${API_BASE}/content-page/subtopics/${id}`,
    subtopic: (id) => `${API_BASE}/content-page/chunks/${id}`,
    chunk: (id) => `${API_BASE}/content-page/micro-chunks/${id}`
};

const TYPE_LABELS = {
    article: '📰 Article/Heading',
    text: '📝 Text',
    image: '🖼️ Image',
    video: '▶️ Video',
    image_carousel: '🖼️🖼️ Image Carousel',
    video_carousel: '▶️▶️ Video Carousel',
    table: '📊 Table',
    numerical: '🔢 Numerical',
    flowchart: '📐 Flowchart',
    html: '🖥️ HTML',
    worksheet: '📄 Worksheet',
};

/**
 * Props:
 *  - level: "topic" | "subtopic" | "chunk" | "micro_chunk"
 *  - entityId: the id of that level's entity
 *  - entityName: display name for breadcrumb
 *  - onDrillInto: (level, id, name) => void  — called when a container card is clicked to drill in
 *  - onBack: () => void  — called to go up one level
 */
const AdminContentEditor = ({ level, entityId, entityName, section, onDrillInto, onBack }) => {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [activeForm, setActiveForm] = useState(null);
    const [editingBlock, setEditingBlock] = useState(null);
    const [showFullView, setShowFullView] = useState(false);
    const [previewingId, setPreviewingId] = useState(null);

    const openNewForm = (type) => {
        setEditingBlock(null);
        setActiveForm(type);
    };

    const openEditForm = (block) => {
        setEditingBlock(block);
        setActiveForm(block.block_type);
    };

    const loadBlocks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(LEVEL_ENDPOINTS[level](entityId, section));
            if (!res.ok) throw new Error('Failed to load blocks');
            const richBlocks = (await res.json()).map(b => ({
                ...b,
                item_kind: 'block',
                rich_content_id: b.rich_content_id ?? b.qa_content_id
            }));

            let containers = [];
            if (CHILD_CONTAINER_ENDPOINT[level]) {
                const containerRes = await fetch(CHILD_CONTAINER_ENDPOINT[level](entityId));
                if (containerRes.ok) {
                    containers = (await containerRes.json()).map(c => ({ ...c, item_kind: 'container' }));
                }
            }

            const merged = [...richBlocks, ...containers].sort((a, b) => a.order_index - b.order_index);
            setBlocks(merged);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [level, entityId, section]);

    useEffect(() => {
        loadBlocks();
    }, [loadBlocks]);

    const deleteBlock = async (richContentId) => {
        if (!window.confirm('Delete this block?')) return;
        try {
            const res = await fetch(`${blockApiBase(level)}/${richContentId}`, { method: 'DELETE' });
            if (!res.ok) throw new Error('Delete failed');
            await loadBlocks();
        } catch (err) {
            setError(err.message);
        }
    };

    const deleteContainer = async (cardType, cardId) => {
        if (!window.confirm('Delete this? It must have no child items or content blocks inside it.')) return;
        try {
            const res = await fetch(`${API_BASE}/content-page/container/${cardType}/${cardId}`, { method: 'DELETE' });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Delete failed');
            }
            await loadBlocks();
        } catch (err) {
            setError(err.message);
        }
    };

    const moveBlock = async (index, direction) => {
        const newBlocks = [...blocks];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        if (swapIndex < 0 || swapIndex >= newBlocks.length) return;

        [newBlocks[index], newBlocks[swapIndex]] = [newBlocks[swapIndex], newBlocks[index]];

        try {
            for (let i = 0; i < newBlocks.length; i++) {
                const item = newBlocks[i];
                const targetOrder = i + 1;
                if (item.item_kind === 'block') {
                    if (item.order_index !== targetOrder) {
                        await fetch(`${blockApiBase(level)}/${item.rich_content_id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ order_index: targetOrder })
                        });
                    }
                } else {
                    if (item.order_index !== targetOrder) {
                        await fetch(`${API_BASE}/content-page/container/reorder`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ card_type: item.card_type, card_id: item.card_id, new_order_index: targetOrder })
                        });
                    }
                }
            }
            await loadBlocks();
        } catch (err) {
            setError('Reorder failed');
        }
    };

    const previewText = (block) => {
        const data = block.content_data?.en || block.content_data || {};
        return stripHtml(data.heading) || data.content || data.caption || data.title || '(no preview)';
    };

    const commonFormProps = {
        level,
        entityId,
        section,
        editingBlock,
        nextOrderIndex: blocks.length + 1,
        onSaved: () => { setActiveForm(null); setEditingBlock(null); loadBlocks(); },
        onCancel: () => { setActiveForm(null); setEditingBlock(null); }
    };

    const getPreviewBlock = (item) => ({
        ...item,
        content_data: { ...item.content_data, ...(item.content_data?.en || {}) }
    });

    return (
        <div className="ace-container">
            <div className="ace-header">
                {onBack && <button className="ace-back-btn" onClick={onBack}>&larr; Back</button>}
                <h2>{entityName || `${level} #${entityId}`}</h2>
                {level === 'topic' && (
                    <button className="ace-back-btn" onClick={() => setShowFullView(true)}>📋 Full View</button>
                )}
            </div>

            {showFullView && (
                <AdminContentFullView topicId={entityId} onClose={() => setShowFullView(false)} />
            )}

            {error && <div className="ace-error">{error}</div>}

            <div className="ace-type-picker">
                {CHILD_LEVEL[level] && (
                    <button className="ace-container-btn" onClick={() => openNewForm('container')}>
                        ➕ Insert {CHILD_LABEL[level]}
                    </button>
                )}
                <button onClick={() => openNewForm('article')}>📰 Article/Heading</button>
                <button onClick={() => openNewForm('text')}>📝 Text</button>
                <button onClick={() => openNewForm('image')}>🖼️ Image</button>
                <button onClick={() => openNewForm('video')}>▶️ Video</button>
                <button onClick={() => openNewForm('table')}>📊 Table</button>
                <button onClick={() => openNewForm('flowchart')}>📐 Flowchart</button>
                <button onClick={() => openNewForm('html')}>🖥️ HTML</button>
                <button onClick={() => openNewForm('steps')}>🪜 Steps</button>
                <button onClick={() => openNewForm('image_carousel')}>🖼️🖼️ Image Carousel</button>
                <button onClick={() => openNewForm('video_carousel')}>▶️▶️ Video Carousel</button>
                <button onClick={() => openNewForm('worksheet')}>📄 Worksheet</button>
            </div>

            <div className="ace-columns">
                <div className="ace-left-col">
                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <div className="ace-block-list">
                            {blocks.length === 0 && <p className="ace-empty">No blocks yet. Add your first block on the right.</p>}

                            {blocks.map((item, index) => (
                                <React.Fragment key={item.item_kind === 'block' ? item.rich_content_id : `${item.card_type}-${item.card_id}`}>
                                    <div className="ace-row">
                                        <div
                                            className="ace-row-line"
                                            onClick={() => item.item_kind === 'container' && onDrillInto(item.card_type, item.card_id, item.heading)}
                                            style={{ cursor: item.item_kind === 'container' ? 'pointer' : 'default' }}
                                        >
                                            <span className="ace-row-icon">
                                                {item.item_kind === 'container' ? '📂' : (TYPE_LABELS[item.block_type]?.split(' ')[0] || '📄')}
                                            </span>
                                            <span className="ace-row-label">
                                                {item.item_kind === 'container' ? item.card_type.replace('_', ' ') : item.block_type}:
                                            </span>
                                            <span className="ace-row-text">
                                                {(item.item_kind === 'container' ? item.heading : previewText(item)).substring(0, 90)}
                                            </span>
                                        </div>
                                        <div className="ace-block-actions">
                                            <button onClick={() => moveBlock(index, 'up')} disabled={index === 0}>↑</button>
                                            <button onClick={() => moveBlock(index, 'down')} disabled={index === blocks.length - 1}>↓</button>
                                            {item.item_kind === 'block' && (
                                                <button onClick={() => setPreviewingId(previewingId === item.rich_content_id ? null : item.rich_content_id)}>👁️</button>
                                            )}
                                            {item.item_kind === 'block' && (
                                                <button onClick={() => openEditForm(item)}>✏️</button>
                                            )}
                                            {item.item_kind === 'block' && (
                                                <button onClick={() => deleteBlock(item.rich_content_id)} className="ace-delete-btn">🗑️</button>
                                            )}
                                            {item.item_kind === 'container' && (
                                                <button onClick={() => { setEditingBlock(item); setActiveForm('container'); }}>✏️</button>
                                            )}
                                            {item.item_kind === 'container' && (
                                                <button onClick={() => deleteContainer(item.card_type, item.card_id)} className="ace-delete-btn">🗑️</button>
                                            )}
                                        </div>
                                    </div>
                                    {item.item_kind === 'block' && previewingId === item.rich_content_id && (
                                        <div className="ace-preview-box">
                                            <ContentBlockRenderer block={getPreviewBlock(item)} onMediaClick={null} />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>

                <div className="ace-right-col">
                    {activeForm === 'container' && (
                        <ContainerForm
                            level={level}
                            childLevel={editingBlock ? editingBlock.card_type : CHILD_LEVEL[level]}
                            entityId={entityId}
                            nextOrderIndex={blocks.length + 1}
                            editingContainer={editingBlock}
                            onSaved={() => { setActiveForm(null); setEditingBlock(null); loadBlocks(); }}
                            onCancel={() => { setActiveForm(null); setEditingBlock(null); }}
                            onDrillInto={onDrillInto}
                        />
                    )}

                    {activeForm === 'text' && <TextForm {...commonFormProps} />}
                    {activeForm === 'article' && <ArticleForm {...commonFormProps} />}
                    {activeForm === 'image' && <ImageForm {...commonFormProps} />}
                    {activeForm === 'video' && <VideoForm {...commonFormProps} />}
                    {activeForm === 'table' && <TableForm {...commonFormProps} />}
                    {activeForm === 'flowchart' && <FlowchartForm {...commonFormProps} />}
                    {activeForm === 'html' && <HtmlForm {...commonFormProps} />}
                    {activeForm === 'steps' && <StepsForm {...commonFormProps} />}
                    {activeForm === 'image_carousel' && <ImageCarouselForm {...commonFormProps} />}
                    {activeForm === 'video_carousel' && <VideoCarouselForm {...commonFormProps} />}
                    {activeForm === 'worksheet' && <WorksheetForm {...commonFormProps} />}
                </div>
            </div>
        </div>
    );
};

export default AdminContentEditor;